import { Injectable } from '@angular/core';
import { TopicLevel2Query } from '@examdojo/category/v2';
import { distinctUntilArrayChanged } from '@examdojo/core/rxjs';
import { QuestionBankUIModel } from '@examdojo/question-bank';
import { QueryEntity } from '@examdojo/state';
import { combineLatest, map } from 'rxjs';
import {
  AssignmentBuilderSectionStoreModel,
  AssignmentBuilderSectionUIModel,
  AssignmentBuilderState,
} from './assignment-builder.model';
import { AssignmentBuilderStore } from './assignment-builder.store';

@Injectable({ providedIn: 'root' })
export class AssignmentBuilderQuery extends QueryEntity<
  AssignmentBuilderState,
  AssignmentBuilderSectionStoreModel,
  AssignmentBuilderSectionUIModel,
  'id'
> {
  constructor(
    protected override readonly store: AssignmentBuilderStore,
    private readonly topicLevel2Query: TopicLevel2Query,
  ) {
    super(store);
  }
  // TODO: Duplication with question-bank.query.ts
  // Find a way to have it in one place (maybe we should calculate it on view level?)
  protected override readonly getAllEntitiesTriggers$ = () => [this.topicLevel2Query.entities$];

  protected override readonly getActiveEntityTriggers$ = (id: string) => [
    combineLatest([
      this.selectEntity(id).pipe(
        map((entity) => entity?.questions?.flatMap((q) => q.topic_level_02_ids || []) || []),
        distinctUntilArrayChanged(),
      ),
      this.topicLevel2Query.entities$,
    ]).pipe(
      map(([questionTopicIds, entities]) => {
        return entities.filter((entity) => questionTopicIds.includes(entity.id));
      }),
      distinctUntilArrayChanged(),
    ),
  ];

  readonly topicsCovered$ = this.entities$.pipe(
    map((sections) => {
      const allTopics = sections.flatMap((section) =>
        section.questions.flatMap((question) => (question.topics ?? []).map((topic) => `${topic.code} ${topic.name}`)),
      );
      return [...new Set(allTopics)].sort((a, b) => a.localeCompare(b));
    }),
    distinctUntilArrayChanged(),
  );

  readonly totalMarks$ = this.entities$.pipe(
    map((sections) => {
      return sections.reduce((sum, section) => sum + section.totalMarks, 0);
    }),
  );

  readonly allQuestions$ = this.entities$.pipe(
    map((sections) => sections.flatMap((section) => section.questions)),
    distinctUntilArrayChanged(),
  );

  getTotalMarks(): number {
    return this.getAllUIEntities().reduce((sum, section) => sum + section.totalMarks, 0);
  }

  override toUIModelFn = (section: AssignmentBuilderSectionStoreModel): AssignmentBuilderSectionUIModel => {
    const questionsWithTopics: QuestionBankUIModel[] = section.questions.map((question) => {
      const topics = (question.topic_level_02_ids ?? [])
        .map((topicId) => {
          const topic = this.topicLevel2Query.getEntity(topicId);
          return {
            id: topicId,
            code: topic?.code ?? '',
            name: topic?.name ?? '',
            isSelected: false,
          };
        })
        .filter((topic) => topic.code !== '');

      return {
        ...question,
        topics,
      };
    });

    return {
      ...section,
      questions: questionsWithTopics,
      totalMarks: section.questions.reduce((sum, question) => {
        return sum + (question.max_points ?? 0);
      }, 0),
      questionCount: section.questions.length,
    };
  };
}
